import ConfirmDialog from "@/components/ConfirmDialog/index.js";
import type {
    RegistrationFieldValues,
    RegistrationTransformedValues,
} from "@/components/RegistrationForm";
import {
    useDeleteRegistrationMutation,
    useUpdateRegistrationMutation,
} from "@/mutations/registration.ts";
import { useQueryOptionsFactory } from "@/queries";
import { defaultMutationErrorHandler } from "@/utils/api.ts";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useConfirm } from "react-confirm-hook";
import type { DefaultValues } from "react-hook-form";
import RegistrationDialog from "../-components/RegistrationDialog.tsx";

const Root = (): ReactNode => {
    const qof = useQueryOptionsFactory();
    const { auctionId, registrationId } = Route.useParams();
    const auction = useSuspenseQuery(qof.auction.get(auctionId)).data;
    const registration = useSuspenseQuery(qof.registration.get(auctionId, registrationId)).data;
    const navigate = Route.useNavigate();
    const updateMutation = useUpdateRegistrationMutation();
    const [open, setOpen] = useState(true);
    const deleteAllowed =
        auction.state === "registration_open" || auction.state === "registration_closed";
    const hasAuctionBegun = auction.state === "running" || auction.state === "completed";
    const deleteMutation = useDeleteRegistrationMutation();
    const confirm = useConfirm(ConfirmDialog);

    const handleDelete = useCallback(() => {
        confirm({
            title: "Delete registration",
            message: "Are you sure that you want to delete this registration?",
            onConfirm: async () => {
                // Navigating away immediately will prevent the router from trying to reload
                // the registration after invalidation.
                void navigate({
                    to: "/manage/auctions/$auctionId/registrations",
                    search: true,
                });

                try {
                    await deleteMutation.mutateAsync({
                        registrationId: registration.id,
                        auctionId: auction.id,
                    });
                } catch (error) {
                    defaultMutationErrorHandler(error);
                }
            },
        });
    }, [confirm, navigate, deleteMutation, registration.id, auction.id]);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const defaultValues = useMemo(
        (): DefaultValues<RegistrationFieldValues> => ({
            ...registration,
            bidder: {
                ...registration.bidder,
                phoneNumber: registration.bidder.phoneNumber.format("E.164"),
            },
            attorney: {
                ...registration.attorney,
                phoneNumber: registration.attorney.phoneNumber?.format("E.164") ?? "",
            },
            lender: {
                ...registration.lender,
                phoneNumber: registration.lender.phoneNumber?.format("E.164") ?? "",
            },
            openingBids: Object.fromEntries(
                registration.openingBids.map((bid) => [
                    `${bid.target}-${bid.index}`,
                    bid.amount.toString(),
                ]),
            ),
        }),
        [registration],
    );

    const handleSubmit = useCallback(
        (values: RegistrationTransformedValues) => {
            updateMutation.mutate(
                { auctionId, registrationId, ...values },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Registration has been updated", { variant: "success" });
                        setOpen(false);
                    },
                    onError: defaultMutationErrorHandler,
                },
            );
        },
        [updateMutation, auctionId, registrationId],
    );

    return (
        <RegistrationDialog
            title="Edit registration"
            open={open}
            onClose={handleClose}
            auction={auction}
            defaultValues={defaultValues}
            navigate={navigate}
            onSubmit={handleSubmit}
            isEdit
            hasAuctionBegun={hasAuctionBegun}
            onDelete={deleteAllowed ? handleDelete : undefined}
        />
    );
};

export const Route = createFileRoute(
    "/manage/auctions/$auctionId/registrations/$registrationId/edit",
)({
    component: Root,
});
